/**
 * Store Locator
 * Interactive Google Maps store finder with search, distance sorting,
 * and store card / marker interaction.
 *
 * Supports two data sources (combined):
 *  1. Theme editor blocks (data-sl-store elements in DOM)
 *  2. CSV file import (data-csv-url attribute on <store-locator>)
 *
 * CSV columns: store_name,street_address,city,state,zip_code,phone,hours,store_type,latitude,longitude
 */
class StoreLocator extends HTMLElement {
  constructor() {
    super();

    this.apiKey = this.dataset.apiKey || '';
    this.csvUrl = this.dataset.csvUrl || '';
    this.defaultLat = parseFloat(this.dataset.defaultLat) || 39.8283;
    this.defaultLng = parseFloat(this.dataset.defaultLng) || -98.5795;
    this.defaultZoom = parseInt(this.dataset.defaultZoom, 10) || 5;

    this.map = null;
    this.markers = [];
    this.infoWindow = null;
    this.stores = [];
    this.activeStoreIndex = null;
    this.searchOrigin = null;
  }

  async connectedCallback() {
    this.parseBlockStores();

    if (this.csvUrl) {
      await this.loadCSVStores();
    }

    // Re-index after all stores are loaded
    this.stores.forEach((s, i) => { s.index = i; });

    this.bindEvents();
    this.updateResultsCount(this.stores.length);

    // Hide empty state if we have stores
    if (this.stores.length > 0) {
      const empty = this.querySelector('[data-sl-empty]');
      if (empty) empty.style.display = 'none';
    }

    this.loadGoogleMaps();
  }

  /* ── Data: Theme Editor Blocks ─────────────────── */

  parseBlockStores() {
    const storeEls = this.querySelectorAll('[data-sl-store]');
    this.stores = Array.from(storeEls).map((el, i) => ({
      el,
      index: i,
      name: el.dataset.slName || '',
      address: el.dataset.slAddress || '',
      city: el.dataset.slCity || '',
      state: el.dataset.slState || '',
      zip: el.dataset.slZip || '',
      phone: el.dataset.slPhone || '',
      hours: el.dataset.slHours || '',
      type: el.dataset.slType || '',
      lat: parseFloat(el.dataset.slLat),
      lng: parseFloat(el.dataset.slLng),
      distance: null,
      marker: null,
    }));
  }

  /* ── Data: CSV Import ──────────────────────────── */

  async loadCSVStores() {
    try {
      const res = await fetch(this.csvUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const rows = this.parseCSV(text);

      const list = this.querySelector('[data-sl-store-list]');
      if (!list) return;

      rows.forEach(row => {
        if (!row.store_name) return;

        const store = {
          name: row.store_name || '',
          address: row.street_address || '',
          city: row.city || '',
          state: row.state || '',
          zip: row.zip_code || '',
          phone: row.phone || '',
          hours: row.hours || '',
          type: row.store_type || '',
          lat: parseFloat(row.latitude),
          lng: parseFloat(row.longitude),
          distance: null,
          marker: null,
          index: 0, // re-indexed later
          el: null,
        };

        // Surface bad-data rows so the merchant can spot them while
        // testing — these still appear in the list but have no map pin.
        if (isNaN(store.lat) || isNaN(store.lng)) {
          console.warn(`[store-locator] "${store.name}" has no valid coordinates — pin will be skipped. Re-run tools/geocode-stores.html on the source CSV.`);
        }

        // Create DOM card
        store.el = this.createStoreCard(store);
        list.appendChild(store.el);
        this.stores.push(store);
      });
    } catch (err) {
      console.warn('Failed to load CSV stores:', err);
    }
  }

  parseCSV(text) {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];

    const headers = this.splitCSVLine(lines[0]).map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));

    return lines.slice(1).map(line => {
      const values = this.splitCSVLine(line);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (values[i] || '').trim(); });
      return obj;
    });
  }

  splitCSVLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result;
  }

  createStoreCard(store) {
    const typeSlug = store.type.toLowerCase().replace(/\s+/g, '-');
    const phoneDigits = store.phone.replace(/[^+\d]/g, '');
    const dirDest = encodeURIComponent(`${store.address} ${store.city} ${store.state} ${store.zip}`);

    const card = document.createElement('div');
    card.className = 'he-sl__store-card';
    card.dataset.slStore = '';
    card.innerHTML = `
      <div class="he-sl__store-card-top">
        <h3 class="he-sl__store-name">${this.escapeHtml(store.name)}</h3>
        <span class="he-sl__store-badge he-sl__store-badge--${typeSlug}">
          ${this.escapeHtml(store.type)}
        </span>
      </div>
      <address class="he-sl__store-address">
        ${this.escapeHtml(store.address)}<br>
        ${this.escapeHtml(store.city)}, ${this.escapeHtml(store.state)} ${this.escapeHtml(store.zip)}
      </address>
      ${store.phone ? `
        <a href="tel:${phoneDigits}" class="he-sl__store-phone">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
          ${this.escapeHtml(store.phone)}
        </a>
      ` : ''}
      ${store.hours ? `
        <span class="he-sl__store-hours">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
          ${this.escapeHtml(store.hours)}
        </span>
      ` : ''}
      <a class="he-sl__store-directions"
         href="https://www.google.com/maps/dir/?api=1&destination=${dirDest}"
         target="_blank" rel="noopener noreferrer">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="3 11 22 2 13 21 11 13 3 11"></polygon></svg>
        Get Directions
      </a>
    `;
    return card;
  }

  /* ── Events ────────────────────────────────────── */

  bindEvents() {
    const searchBtn = this.querySelector('[data-sl-search-btn]');
    if (searchBtn) searchBtn.addEventListener('click', () => this.handleSearch());

    const searchInput = this.querySelector('[data-sl-search-input]');
    if (searchInput) {
      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this.handleSearch();
      });
      searchInput.addEventListener('input', () => {
        const clearBtn = this.querySelector('[data-sl-search-clear]');
        if (clearBtn) clearBtn.style.display = searchInput.value ? '' : 'none';
      });
    }

    const clearBtn = this.querySelector('[data-sl-search-clear]');
    if (clearBtn) clearBtn.addEventListener('click', () => this.clearSearch());

    const resetBtn = this.querySelector('[data-sl-reset]');
    if (resetBtn) resetBtn.addEventListener('click', () => this.clearSearch());

    this.stores.forEach((store) => {
      store.el.addEventListener('click', (e) => {
        if (e.target.closest('a')) return;
        this.selectStore(store.index);
      });
    });
  }

  /* ── Google Maps Loading ───────────────────────── */

  loadGoogleMaps() {
    if (!this.apiKey) {
      this.showMapError('Google Maps API key is missing. Add it in the theme editor.');
      return;
    }

    if (window.google && window.google.maps) {
      this.initMap();
      return;
    }

    const callbackName = `__storeLocatorInit_${Date.now()}`;
    window[callbackName] = () => {
      delete window[callbackName];
      this.initMap();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.apiKey)}&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      this.showMapError('Failed to load Google Maps. Check your API key.');
    };
    document.head.appendChild(script);
  }

  /* ── Map Initialization ────────────────────────── */

  initMap() {
    const mapEl = this.querySelector('[data-sl-map]');
    const loadingEl = this.querySelector('[data-sl-map-loading]');

    this.map = new google.maps.Map(mapEl, {
      center: { lat: this.defaultLat, lng: this.defaultLng },
      zoom: this.defaultZoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        { featureType: 'water', stylers: [{ color: '#c9e4f4' }] },
        { featureType: 'road', elementType: 'geometry', stylers: [{ lightness: 30 }] },
      ],
    });

    this.infoWindow = new google.maps.InfoWindow();

    this.addMarkers();

    if (this.stores.length > 0) {
      this.fitBounds();
    }

    if (loadingEl) loadingEl.style.display = 'none';
    this.updateResultsCount(this.stores.length);
  }

  /* ── Markers ───────────────────────────────────── */

  addMarkers() {
    this.markers.forEach(m => m.setMap(null));
    this.markers = [];

    this.stores.forEach((store, i) => {
      if (isNaN(store.lat) || isNaN(store.lng)) return;

      const marker = new google.maps.Marker({
        position: { lat: store.lat, lng: store.lng },
        map: this.map,
        title: store.name,
      });

      marker.addListener('click', () => this.selectStore(i));
      this.markers.push(marker);
      store.marker = marker;
    });
  }

  /* ── Store Selection ───────────────────────────── */

  selectStore(index) {
    const store = this.stores[index];
    if (!store || !store.marker) return;

    // Deactivate previous
    if (this.activeStoreIndex !== null) {
      const prev = this.stores[this.activeStoreIndex];
      if (prev) prev.el.classList.remove('he-sl__store-card--active');
    }

    // Activate
    store.el.classList.add('he-sl__store-card--active');
    this.activeStoreIndex = index;
    store.el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Map
    this.map.panTo(store.marker.getPosition());
    if (this.map.getZoom() < 13) this.map.setZoom(13);

    // Info window
    this.infoWindow.setContent(this.buildInfoWindowContent(store));
    this.infoWindow.open(this.map, store.marker);
  }

  buildInfoWindowContent(store) {
    const dest = encodeURIComponent(`${store.address} ${store.city} ${store.state} ${store.zip}`);
    return `
      <div style="font-family:inherit;max-width:260px;padding:6px 4px;">
        <strong style="font-size:14px;color:#0a0a0a;letter-spacing:-0.005em;">${this.escapeHtml(store.name)}</strong>
        <p style="font-size:12px;margin:6px 0;color:#555;line-height:1.45;">
          ${this.escapeHtml(store.address)}<br>
          ${this.escapeHtml(store.city)}, ${this.escapeHtml(store.state)} ${this.escapeHtml(store.zip)}
        </p>
        ${store.phone ? `<p style="font-size:12px;margin:3px 0;"><a href="tel:${store.phone.replace(/[^+\d]/g, '')}" style="color:#0a0a0a;text-decoration:none;">${this.escapeHtml(store.phone)}</a></p>` : ''}
        ${store.hours ? `<p style="font-size:11px;margin:3px 0;color:#888;">${this.escapeHtml(store.hours)}</p>` : ''}
        <a href="https://www.google.com/maps/dir/?api=1&destination=${dest}" target="_blank" rel="noopener"
           style="display:inline-block;margin-top:8px;font-size:12px;font-weight:600;color:#0a0a0a;text-decoration:none;border-bottom:1px solid #b8a066;padding-bottom:1px;">
          Get Directions &rarr;
        </a>
      </div>
    `;
  }

  /* ── Search ────────────────────────────────────── */

  async handleSearch() {
    const input = this.querySelector('[data-sl-search-input]');
    const query = input?.value.trim();
    if (!query) return;

    const geocoder = new google.maps.Geocoder();

    try {
      const results = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: query }, (results, status) => {
          if (status === 'OK' && results.length > 0) resolve(results);
          else reject(new Error(status));
        });
      });

      const location = results[0].geometry.location;
      this.searchOrigin = { lat: location.lat(), lng: location.lng() };

      // Calculate distances
      this.stores.forEach(store => {
        if (!isNaN(store.lat) && !isNaN(store.lng)) {
          store.distance = this.calculateDistance(
            this.searchOrigin.lat, this.searchOrigin.lng,
            store.lat, store.lng
          );
        } else {
          store.distance = Infinity;
        }
      });

      // Sort cards by distance
      const sorted = [...this.stores].sort((a, b) => a.distance - b.distance);
      const list = this.querySelector('[data-sl-store-list]');

      sorted.forEach(store => {
        list.appendChild(store.el);

        let distEl = store.el.querySelector('.he-sl__store-distance');
        if (!distEl) {
          distEl = document.createElement('span');
          distEl.className = 'he-sl__store-distance';
          store.el.appendChild(distEl);
        }
        distEl.textContent = store.distance < Infinity
          ? `${store.distance.toFixed(1)} miles away`
          : '';
      });

      // Center map
      this.map.setCenter(this.searchOrigin);
      this.map.setZoom(9);

      // No results feedback
      const noResults = this.querySelector('[data-sl-no-results]');
      if (sorted[0]?.distance > 100) {
        if (noResults) noResults.style.display = '';
      } else {
        if (noResults) noResults.style.display = 'none';
      }

      this.updateResultsCount(this.stores.length);

    } catch (err) {
      console.warn('Geocoding failed:', err);
    }
  }

  clearSearch() {
    const input = this.querySelector('[data-sl-search-input]');
    if (input) input.value = '';

    const clearBtn = this.querySelector('[data-sl-search-clear]');
    if (clearBtn) clearBtn.style.display = 'none';

    const noResults = this.querySelector('[data-sl-no-results]');
    if (noResults) noResults.style.display = 'none';

    // Remove distance badges and reset order
    this.stores.forEach(store => {
      store.distance = null;
      const distEl = store.el.querySelector('.he-sl__store-distance');
      if (distEl) distEl.remove();
    });

    const list = this.querySelector('[data-sl-store-list]');
    [...this.stores]
      .sort((a, b) => a.index - b.index)
      .forEach(store => list.appendChild(store.el));

    this.searchOrigin = null;

    if (this.stores.length > 0) {
      this.fitBounds();
    } else {
      this.map.setCenter({ lat: this.defaultLat, lng: this.defaultLng });
      this.map.setZoom(this.defaultZoom);
    }

    if (this.infoWindow) this.infoWindow.close();

    if (this.activeStoreIndex !== null) {
      this.stores[this.activeStoreIndex]?.el.classList.remove('he-sl__store-card--active');
      this.activeStoreIndex = null;
    }

    this.updateResultsCount(this.stores.length);
  }

  /* ── Helpers ───────────────────────────────────── */

  fitBounds() {
    const bounds = new google.maps.LatLngBounds();
    let hasValid = false;
    this.stores.forEach(store => {
      if (!isNaN(store.lat) && !isNaN(store.lng)) {
        bounds.extend({ lat: store.lat, lng: store.lng });
        hasValid = true;
      }
    });
    if (hasValid) this.map.fitBounds(bounds, { padding: 50 });
  }

  updateResultsCount(count) {
    const el = this.querySelector('[data-sl-results-count]');
    if (el) el.textContent = `${count} store${count !== 1 ? 's' : ''} found`;
  }

  showMapError(message) {
    const loadingEl = this.querySelector('[data-sl-map-loading]');
    if (loadingEl) {
      loadingEl.innerHTML = `<p style="color:#c00;font-size:0.9rem;">${message}</p>`;
    }
  }

  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 3959; // Earth radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  toRad(deg) {
    return deg * (Math.PI / 180);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
